import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/seo-vs-ppc");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/seo-vs-ppc")({
  head: () => ({
    meta: [
        { name: "description", content: "SEO vs Google Ads: which should a Colorado small business invest in first? Real timelines, costs, and ROI comparisons from a Colorado agency." },
      {
        title: "SEO vs Google Ads for Colorado Small Businesses",
      },
      {
        name: "description",
        content:
          "Honest budget-by-budget breakdown for Colorado small businesses. When PPC wins, when SEO wins, and when you actually need both.",
      },
      {
        property: "og:title",
        content: "SEO vs Google Ads for Colorado Small Businesses",
      },
      {
        property: "og:description",
        content:
          "Plain-English comparison from a 25-year, in-house Colorado agency.",
      },
      { property: "og:url", content: `${SITE_URL}/learn/seo-vs-ppc` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/seo-vs-ppc" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Marketing Strategy"
      read="9 min read"
      title="SEO vs Google Ads: which one should a Colorado small business invest in first?"
      intro="Wrong question. The right question is: what stage is your business at, and what&rsquo;s your monthly budget? Here&rsquo;s the honest answer at every level."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "SEO vs Google Ads" },
      ]}
      path="/learn/seo-vs-ppc"
      faqs={[
        { q: "Should I do SEO or Google Ads first?", a: "If you need leads in the next 30 days, start with Google Ads. If you can wait 90+ days for compounding results that don't disappear when you stop paying, start with SEO. Most growing businesses do both." },
        { q: "Is SEO really cheaper than PPC long-term?", a: "Yes — once you rank, every click is free. A page ranking #1 for a $20 PPC keyword can save you thousands a month for years. The catch is the 6–12 months of work to get there." },
        { q: "Can I stop SEO once I'm ranking?", a: "You can scale it back, but not stop. Competitors are still working, Google updates change the rules, and content gets stale. Maintenance SEO is far cheaper than rebuilding lost rankings." },
      ]}
    >
      <h2>The 30-second version</h2>
      <ul>
        <li><strong>Need leads tomorrow?</strong> Google Ads (PPC).</li>
        <li><strong>Want compounding leads in 6–12 months at a fraction of the cost?</strong> SEO.</li>
        <li><strong>Have $2,000+/month and want to grow seriously?</strong> Both.</li>
      </ul>

      <h2>When PPC is the right first move</h2>
      <ul>
        <li>You&rsquo;re a brand-new business with no Google Business Profile history.</li>
        <li>Your sales cycle is short and your average ticket is high enough to absorb $20–$80 per click in competitive Colorado markets.</li>
        <li>You&rsquo;ve got a launch, a seasonal window, or capacity you need to fill <em>this month</em>.</li>
        <li>You want to test which services or cities actually convert before investing in 30 SEO pages.</li>
      </ul>

      <h2>When SEO is the right first move</h2>
      <ul>
        <li>You&rsquo;ve been in business 1+ years and already have some reviews and a Google Business Profile.</li>
        <li>Your service has steady, year-round demand (HVAC, plumbing, legal, medical, etc.).</li>
        <li>You can&rsquo;t afford to keep paying $40+ per lead forever — you want compounding traffic.</li>
        <li>You want to dominate your service area long-term, not rent visibility.</li>
      </ul>

      <h2>Honest cost comparison (Colorado averages)</h2>
      <ul>
        <li><strong>Google Ads:</strong> $20–$80 per click in competitive home-service markets. Cost-per-lead typically $40–$200. Budget floor that actually moves the needle: $1,500/month.</li>
        <li><strong>SEO:</strong> $750–$2,500/month for real work. Cost-per-lead drops as rankings build — often under $20/lead by year 2.</li>
        <li><strong>Local SEO + Google Maps</strong> alone: often the highest-ROI single line item in the entire marketing stack.</li>
      </ul>

      <h2>The combo most Colorado small businesses actually need</h2>
      <p>
        For most established Colorado service businesses with $2,000–$5,000/month in marketing
        budget, the right split is roughly:
      </p>
      <ul>
        <li><strong>40–50% SEO + Google Maps</strong> — the long-term compounding asset.</li>
        <li><strong>40–50% Google Ads</strong> — to fill capacity now while SEO ramps.</li>
        <li><strong>10% reviews + content</strong> — feeds both.</li>
      </ul>

      <h2>What this looks like in practice</h2>
      <p>
        We have full pricing on{" "}
        <Link to="/colorado-springs-seo" className="font-bold text-brand-gold-deep underline">
          our SEO page
        </Link>
        ,{" "}
        <Link to="/colorado-springs-ppc" className="font-bold text-brand-gold-deep underline">
          our PPC page
        </Link>
        , and{" "}
        <Link to="/colorado-springs-google-maps" className="font-bold text-brand-gold-deep underline">
          our Google Maps page
        </Link>
        . Or see our{" "}
        <Link to="/pricing" className="font-bold text-brand-gold-deep underline">
          pricing page
        </Link>{" "}
        for the three packages.
      </p>

      <h2>One last thing</h2>
      <p>
        If an agency tells you to put your entire budget into one channel, ask why. The honest
        answer for almost every Colorado small business is &ldquo;a little of both, weighted toward
        whichever you need most right now.&rdquo; Anyone selling you a single-channel silver bullet
        is selling what&rsquo;s easiest for <em>them</em>, not what&rsquo;s best for you.
      </p>
    </ArticleLayout>
  );
}
